import { WORK_PERMIT_STATUS } from '../consts'

/**
 * @typedef {Object} ResolvedHistoryCommentContent
 * @property {string|null} content - Internal / engineer-facing display text.
 * @property {string|null} externalContent - Tenant/contractor-facing display text.
 */

/**
 * @typedef {Object} WorkPermitHistoryDisplayDecision
 * @property {string} layout - One of WORK_PERMIT_HISTORY_DISPLAY_LAYOUT.
 */

export const WORK_PERMIT_HISTORY_DISPLAY_LAYOUT = {
  ENGINEER_APPROVER_SINGLE: 'engineerApproverSingle',
  ENGINEER_REVIEWER_SPLIT: 'engineerReviewerSplit',
  ENGINEER_INTERNAL_OR_EMPTY: 'engineerInternalOrEmpty',
  TENANT_EXTERNAL_ONLY: 'tenantExternalOnly',
  TENANT_APPROVER_SINGLE: 'tenantApproverSingle',
  HIDDEN: 'hidden',
}

export const WP_REVIEWER_HISTORY_STATUSES = [
  WORK_PERMIT_STATUS.ACKNOWLEDGED,
  WORK_PERMIT_STATUS.REVIEWED,
  WORK_PERMIT_STATUS.REVIEWED2,
]

export const WP_HISTORY_DISPLAY_STATUSES = [
  ...WP_REVIEWER_HISTORY_STATUSES,
  WORK_PERMIT_STATUS.REJECTED,
  WORK_PERMIT_STATUS.APPR,
]

export const WP_EXTERNAL_VIEW_HISTORY_STATUSES = [
  WORK_PERMIT_STATUS.APPR,
  WORK_PERMIT_STATUS.REJECTED,
  WORK_PERMIT_STATUS.COMPLETED,
  WORK_PERMIT_STATUS.EXPIRED,
]

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Runtime guard helper for optional string fields in history contracts.
 * @param {*} value
 * @returns {boolean}
 */
export function isNullableString (value) {
  return value == null || typeof value === 'string'
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Runtime guard for the resolved history comment display contract.
 * @param {*} value
 * @returns {value is ResolvedHistoryCommentContent}
 */
export function isResolvedHistoryCommentContent (value) {
  return value != null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.prototype.hasOwnProperty.call(value, 'content') &&
    Object.prototype.hasOwnProperty.call(value, 'externalContent') &&
    isNullableString(value.content) &&
    isNullableString(value.externalContent)
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Create the final `{ content, externalContent }` display contract.
 * @param {Object} value
 * @param {string|null} [value.content]
 * @param {string|null} [value.externalContent]
 * @returns {ResolvedHistoryCommentContent}
 */
export function createResolvedHistoryCommentContent ({
  content = '',
  externalContent = null,
} = {}) {
  const resolved = {
    content: content == null ? null : String(content),
    externalContent: externalContent == null ? null : String(externalContent),
  }
  return resolved
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Runtime guard for history display decision output.
 * @param {*} value
 * @returns {value is WorkPermitHistoryDisplayDecision}
 */
export function isWorkPermitHistoryDisplayDecision (value) {
  return value != null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.values(WORK_PERMIT_HISTORY_DISPLAY_LAYOUT).includes(value.layout)
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Create a stable history display decision contract.
 * @param {Object} value
 * @param {string} value.layout
 * @returns {WorkPermitHistoryDisplayDecision}
 */
export function createWorkPermitHistoryDisplayDecision ({
  layout = WORK_PERMIT_HISTORY_DISPLAY_LAYOUT.HIDDEN,
} = {}) {
  const decision = {
    layout,
  }
  if (!isWorkPermitHistoryDisplayDecision(decision)) {
    return {
      layout: WORK_PERMIT_HISTORY_DISPLAY_LAYOUT.HIDDEN,
    }
  }
  return decision
}
